/**
 * NAT-2497 — which venue the signed-in person is acting for.
 *
 * Selection is client-side, sent on every request and validated server-side
 * every time (spec §6). Nothing here authorises anything: the server refuses
 * a venue the caller does not hold. What this decides is what to render and
 * what id to send:
 *
 *   - a customer's actable set is `GET /customer/my-clients`; with none it
 *     gets an explained empty state, with one it acts for it silently, with
 *     several it must name one — per tab, seeded from a startup preference
 *   - an agent or admin keeps its own picker and is never gated: an absent
 *     selection is unscoped browsing, which the server allows for those roles
 *   - a selection the grants no longer contain is never kept, so a revoked
 *     venue does not stay selected on the strength of a stale tab
 */

export type Grant = { id: string; name: string | null; slug: string | null };

export type ActingRole = 'customer' | 'acting' | 'other';

export type ActingStatus = 'loading' | 'error' | 'none' | 'choose' | 'ready';

export type ActingResolution = {
  status: ActingStatus;
  /** The id to send with every client-scoped request; null means "none". */
  clientId: string | null;
  /** Whether the header shows the venue selector. */
  showSelector: boolean;
};

const held = (grants: Grant[], id: string | null | undefined): string | null =>
  id != null && id !== '' && grants.some((g) => String(g.id) === String(id)) ? String(id) : null;

export function resolveActingClient(params: {
  role: ActingRole;
  /** `undefined` while the request has not settled — distinct from `[]`. */
  grants: Grant[] | undefined;
  /** This tab's selection. */
  stored: string | null;
  /** The startup preference, shared across tabs. */
  preference: string | null;
  /**
   * The grants on hand cannot be trusted: the request failed and has not
   * since succeeded. Set it for a failure with no list at all, and for one
   * that follows a server refusal — there, a cached list may name a venue
   * that has just been revoked, so it outranks the cache.
   */
  error?: boolean;
}): ActingResolution {
  const { role, grants, stored, preference, error } = params;

  if (role === 'acting') {
    return { status: 'ready', clientId: stored || null, showSelector: false };
  }
  if (role !== 'customer') {
    return { status: 'ready', clientId: null, showSelector: false };
  }

  // A failed request is its own state, not an endless loading screen: the
  // person is told, and offered a retry and a way out. Checked before the
  // cached list, because a stale list is exactly what the caller is telling us
  // not to act on.
  if (error) return { status: 'error', clientId: null, showSelector: false };
  if (grants === undefined) {
    return { status: 'loading', clientId: null, showSelector: false };
  }
  if (grants.length === 0) return { status: 'none', clientId: null, showSelector: false };
  if (grants.length === 1) {
    return { status: 'ready', clientId: String(grants[0].id), showSelector: false };
  }

  const clientId = held(grants, stored) ?? held(grants, preference);
  return { status: clientId ? 'ready' : 'choose', clientId, showSelector: true };
}

/**
 * After the server refused a request mid-session, the grants are reloaded and
 * the selection re-checked (spec §6): a selection the reloaded grants no
 * longer contain is cleared, so the page re-gates instead of retrying a venue
 * that was taken away.
 */
export function afterRefusal(params: {
  grants: Grant[] | undefined;
  current: string | null;
}): string | null {
  const { grants, current } = params;
  if (grants === undefined) return current;
  return held(grants, current);
}
